import { useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { CircularProgress } from "@mui/material";
import { closeSnackbar } from "notistack";

import UserNameField from "../../Components/Form Fields/username-field";
import OTPField from "../../Components/Form Fields/otp-field";
import PrimaryButton from "../../Components/Buttons/primary-button";

import {
  useAuthorizePlaidCoreExchange,
  useLoginWith2FACode,
  useRequestLogin2FACode,
} from "../../CustomHooks/Mutations";

import { APIhandler } from "../../Utils/api";
import { appUtilityKeys, debugProps } from "../../Utils/constants";
import {
  dispatchErrorToast,
  dispatchInfoToast,
  dispatchSuccessToast,
  LocalStorage,
  onSignOut,
  saveAndUpdateUserOnLogin,
  transformUSNumberToCountryCodeFormat,
} from "../../Utils/helpers";

import { StakeLogo } from "../../Assets/Images";

import {
  getRedirectUriFromResponse,
  getSafeRedirectUri,
  isValidAuthRequestId,
} from "./plaid-core-exchange-utils";

const GENERIC_ERROR_MESSAGE = "We couldn't complete this account connection. Please return to the app you came from.";

export default function PlaidCoreExchangeAuthorize() {
  const resendOtpRef = useRef(false);
  const submissionLockRef = useRef(false);

  const [step, setStep] = useState("phone");
  const [terminalMessage, setTerminalMessage] = useState("");

  const formProps = useForm();

  const authRequestId = useMemo(() => new URLSearchParams(window.location.search).get("auth_request_id"), []);
  const hasValidAuthRequestId = isValidAuthRequestId(authRequestId);

  const {
    handleSubmit,
    getValues,
    watch,
    resetField,
    formState: { isValid },
  } = formProps;

  const userName = watch("username");

  const redirectToSynctera = (response) => {
    const redirectUri = getSafeRedirectUri(getRedirectUriFromResponse(response));
    if (!redirectUri) {
      throw new Error("The authorization response did not include a safe redirect URI.");
    }
    window.location.replace(redirectUri);
  };

  const { isLoading: isAuthorizing, mutateAsync: authorizePlaidCoreExchange } = useAuthorizePlaidCoreExchange();

  const completeAuthorization = async (status) => {
    if (submissionLockRef.current || !hasValidAuthRequestId) return;

    submissionLockRef.current = true;
    setStep("authorizing");
    setTerminalMessage("");

    try {
      const response = await authorizePlaidCoreExchange({
        auth_request_id: authRequestId,
        status,
      });
      redirectToSynctera(response);
    } catch (error) {
      submissionLockRef.current = false;
      setStep("error");
      setTerminalMessage(error?.response?.data?.message ?? GENERIC_ERROR_MESSAGE);
    }
  };

  const grantAuthorization = async () => {
    if (submissionLockRef.current || !hasValidAuthRequestId) return;

    submissionLockRef.current = true;
    setStep("authorizing");
    setTerminalMessage("");

    try {
      const transferTokenResponse = await APIhandler.tokens.getTransferToken();
      const transferToken = transferTokenResponse?.data?.data?.token;

      if (!transferToken) {
        throw new Error("A transfer token was not returned.");
      }

      LocalStorage.write(appUtilityKeys.transferToken, transferToken);

      const response = await authorizePlaidCoreExchange({
        auth_request_id: authRequestId,
        status: "GRANTED",
      });
      redirectToSynctera(response);
    } catch (error) {
      const failedBeforeAuthorization = !LocalStorage.read(appUtilityKeys.transferToken);

      if (failedBeforeAuthorization) {
        try {
          const response = await authorizePlaidCoreExchange({
            auth_request_id: authRequestId,
            status: "ERROR",
          });
          redirectToSynctera(response);
          return;
        } catch {
          // Fall through to the generic error state when Synctera cannot provide a redirect.
        }
      }

      submissionLockRef.current = false;
      setStep("error");
      setTerminalMessage(error?.response?.data?.message ?? GENERIC_ERROR_MESSAGE);
    }
  };

  const { isLoading: isRequestingCode, mutate: requestLogin2FACode } = useRequestLogin2FACode({
    onSuccess: ({ data }) => {
      setStep("otp");
      resetField("otp");

      if (resendOtpRef.current) {
        dispatchSuccessToast(`New OTP code was sent to ${userName}`, { key: "plaidCoreExchangeRequest2FA" });
      } else {
        dispatchInfoToast(data?.data?.msg, { key: "plaidCoreExchangeRequest2FA" });
      }
    },
    onError: () => dispatchErrorToast("We couldn't send a verification code. Please try again."),
    onSettled: () => {
      resendOtpRef.current = false;
    },
  });

  const { isLoading: isLoggingIn, mutate: loginWith2FACode } = useLoginWith2FACode({
    onSuccess: async (data) => {
      onSignOut({ redirectOnLogout: false });
      saveAndUpdateUserOnLogin(data?.data);
      await grantAuthorization();
    },
    onError: (error) =>
      dispatchErrorToast(error?.response?.data?.error?.msg ?? "That verification code could not be confirmed.", {
        key: "plaidCoreExchangeLogin",
      }),
  });

  const isLoading = isRequestingCode || isLoggingIn || isAuthorizing || step === "authorizing";

  const onSubmit = () => {
    closeSnackbar();

    const payload = {
      ...debugProps,
      phoneNumber: transformUSNumberToCountryCodeFormat(userName),
    };

    if (step === "phone" || resendOtpRef.current) {
      if (!isLoading) requestLogin2FACode(payload);
      return;
    }

    payload.phoneNumberCode = getValues("otp");
    if (!isLoading) loginWith2FACode(payload);
  };

  const onFormError = (errors) => {
    const message = step === "phone" ? errors.username?.message : errors.otp?.message;
    dispatchErrorToast(message ?? "Please check the information you entered.");
  };

  if (!hasValidAuthRequestId) {
    return (
      <AuthorizationStatus
        title="Invalid authorization link"
        message="This link is missing a valid authorization request. Please return to the app you came from and try again."
      />
    );
  }

  if (step === "authorizing") {
    return (
      <AuthorizationStatus
        loading
        title="Connecting your account"
        message="Keep this window open while we securely finish your connection."
      />
    );
  }

  if (step === "error") {
    return (
      <AuthorizationStatus title="Unable to connect" message={terminalMessage}>
        <PrimaryButton type="button" onClick={grantAuthorization} disabled={isLoading}>
          Try again
        </PrimaryButton>
      </AuthorizationStatus>
    );
  }

  const disableSubmitButton = step === "phone" ? !(userName ?? "") : !isValid;

  return (
    <div className="onboarding-page login plaid-core-exchange justify-content-center">
      <FormProvider {...formProps}>
        <div className="login-card">
          <form
            className={`form-card${step === "otp" ? " otp-state" : ""}`}
            onSubmit={handleSubmit(onSubmit, onFormError)}
            autoComplete="off"
          >
            <div className="card-header">
              <StakeLogo className="login-logo" />
              <h1 className={step === "otp" ? "otp-title" : "login-title"}>
                {step === "otp" ? "Verify your identity" : "Connect your Stake account"}
              </h1>
              <p className={step === "otp" ? "otp-subtitle" : "login-subtitle"}>
                {step === "otp"
                  ? `Enter the 6-digit verification code sent to ${userName}.`
                  : "Sign in with a fresh verification code to securely share your account information."}
              </p>
            </div>

            <div className={`${step === "phone" ? "username" : "otp"}-field`}>
              {step === "phone" ? (
                <>
                  <label htmlFor="username">Phone number</label>
                  <UserNameField disabled={isLoading} />
                </>
              ) : (
                <>
                  <OTPField />
                  <button
                    className="resend-otp-button"
                    onClick={() => {
                      resendOtpRef.current = true;
                      onSubmit();
                    }}
                    type="button"
                    disabled={isLoading}
                  >
                    Didn't receive a text? Resend code
                  </button>
                </>
              )}
            </div>

            <PrimaryButton className="w-100" type="submit" disabled={disableSubmitButton || isLoading}>
              {step === "phone" ? "Continue" : "Verify and connect"}
              {isLoading && <CircularProgress />}
            </PrimaryButton>

            <button
              className="core-exchange-cancel"
              type="button"
              onClick={() => completeAuthorization("DENIED")}
              disabled={isLoading}
            >
              Cancel
            </button>
          </form>
        </div>
      </FormProvider>
    </div>
  );
}

function AuthorizationStatus({ children, loading = false, message, title }) {
  return (
    <div className="onboarding-page login plaid-core-exchange justify-content-center">
      <div className="login-card">
        <div className="form-card core-exchange-status">
          <StakeLogo className="login-logo" />
          {loading && <CircularProgress sx={{ color: "var(--mist)" }} />}
          <h1 className="login-title">{title}</h1>
          <p className="login-subtitle">{message}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
